import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Eye, Edit3, Trash2, PauseCircle, Download, XCircle, Shield, Database, Globe, Mail } from 'lucide-react';
import { GdprHero } from '../components/PageHeroes';
import { useTheme } from '../context/ThemeContext';

const Gdpr = () => {
  const { isDarkMode } = useTheme();

  const compliancePoints = [
    { title: 'Data minimisation', description: 'We collect only the data strictly necessary to deliver our services — nothing more.' },
    { title: 'Purpose limitation', description: 'Personal data is used exclusively for the purposes clearly stated at the time of collection.' },
    { title: 'Storage limitation', description: 'Data is retained only for as long as legally required, then securely and permanently deleted.' },
    { title: 'Integrity & confidentiality', description: 'End-to-end encryption and strict access controls protect every byte of your data.' },
    { title: 'Accountability', description: 'Full audit trails and documentation ensure we can demonstrate compliance at any time.' },
    { title: 'Lawful processing', description: 'All processing activities have a documented lawful basis under UK GDPR Article 6.' }
  ];

  const userRights = [
    { icon: Eye, title: 'Right to access', description: 'Request a full copy of all personal data we hold about you. We respond within 30 days.' },
    { icon: Edit3, title: 'Right to rectification', description: 'Ask us to correct any inaccurate or incomplete personal data we hold.' },
    { icon: Trash2, title: 'Right to erasure', description: 'Request deletion of your data under the "right to be forgotten" where applicable.' },
    { icon: PauseCircle, title: 'Right to restrict processing', description: 'Ask us to pause or limit how we use your personal data in certain circumstances.' },
    { icon: Download, title: 'Right to data portability', description: 'Receive your personal data in a structured, machine-readable format to use elsewhere.' },
    { icon: XCircle, title: 'Right to object', description: 'Object to processing of your data for direct marketing or legitimate interests.' }
  ];

  const dpaItems = [
    { icon: Shield, title: 'Data processor role', description: 'When you use Effective Global Staff to process payroll, financial, or employee data, we act as a data processor on your behalf. All activity is governed by a formal Data Processing Agreement (DPA) that defines our obligations and yours.' },
    { icon: Database, title: 'Sub-processors', description: 'We work with carefully vetted sub-processors for hosting, backup, and analytics. Every sub-processor is contractually bound to protect your data with security measures equivalent to our own.' },
    { icon: Globe, title: 'Data transfers', description: 'All personal data is processed and stored within the UK. We do not transfer data outside the UK without your explicit authorisation and only where adequate protections exist under UK GDPR.' }
  ];

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.07, delayChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } } };

  const p = {
    sectionBg: isDarkMode ? '#0d1117' : 'white',
    sectionAltBg: isDarkMode ? '#161b22' : '#f8fafc',
    cardBg: isDarkMode ? '#161b22' : 'white',
    cardBorder: isDarkMode ? '#30363d' : '#e2e8f0',
    heading: isDarkMode ? '#e6edf3' : '#0f1d3a',
    body: isDarkMode ? '#8b949e' : '#64748b',
    eyebrowBg: isDarkMode ? 'rgba(13, 162, 124, 0.15)' : '#e3f7f0',
    eyebrowText: isDarkMode ? '#2dd4bf' : '#0b7e62',
    iconBg: isDarkMode ? 'rgba(13, 162, 124, 0.15)' : 'rgba(13, 162, 124, 0.1)',
    numberColor: isDarkMode ? '#2dd4bf' : '#0b7e62',
    footBg: isDarkMode ? 'rgba(13, 162, 124, 0.1)' : '#e3f7f0',
    divider: isDarkMode ? '#30363d' : '#e2e8f0',
  };

  const sectionHeader = (eyebrow, title, subtitle) => (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      style={{ textAlign: 'center', maxWidth: '640px', margin: '0 auto 2.5rem' }}
    >
      <span style={{ display: 'inline-block', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: p.eyebrowText, background: p.eyebrowBg, padding: '0.35rem 1rem', borderRadius: '999px', marginBottom: '1rem' }}>
        {eyebrow}
      </span>
      <h2 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 700, color: p.heading, margin: '0 0 0.75rem' }}>{title}</h2>
      {subtitle && <p style={{ fontSize: '0.98rem', color: p.body, margin: 0, lineHeight: 1.65 }}>{subtitle}</p>}
    </motion.div>
  );

  return (
    <div>
      <GdprHero />

      {/* COMPLIANCE PRINCIPLES */}
      <section style={{ padding: '4.5rem 1.5rem', background: p.sectionBg }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>
          {sectionHeader('Our commitment', 'GDPR compliance principles', 'Effective Global Staff is fully compliant with the General Data Protection Regulation and the UK Data Protection Act 2018. Every process is built around protecting your data.')}

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: 'flex', flexDirection: 'column' }}>
            {compliancePoints.map((point, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                style={{ display: 'flex', gap: '1.5rem', padding: '1.25rem 0', borderBottom: idx === compliancePoints.length - 1 ? 'none' : `1px solid ${p.divider}` }}
              >
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: p.numberColor, flexShrink: 0, minWidth: '28px' }}>{String(idx + 1).padStart(2, '0')}</span>
                <div>
                  <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: p.heading, margin: '0 0 0.35rem' }}>{point.title}</h3>
                  <p style={{ fontSize: '0.92rem', color: p.body, margin: 0, lineHeight: 1.65 }}>{point.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* YOUR RIGHTS */}
      <section style={{ padding: '4.5rem 1.5rem', background: p.sectionAltBg }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {sectionHeader('Data subject rights', 'Your GDPR rights', 'Under UK GDPR you have the following rights over your personal data.')}

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
            {userRights.map((right, idx) => {
              const Icon = right.icon;
              return (
                <motion.div key={idx} variants={itemVariants} style={{ display: 'flex', gap: '1rem', background: p.cardBg, border: `1px solid ${p.cardBorder}`, borderRadius: '0.875rem', padding: '1.5rem' }}>
                  <div style={{ width: '42px', height: '42px', borderRadius: '0.65rem', background: p.iconBg, color: '#0da27c', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '0.98rem', fontWeight: 700, color: p.heading, margin: '0 0 0.4rem' }}>{right.title}</h3>
                    <p style={{ fontSize: '0.86rem', color: p.body, margin: 0, lineHeight: 1.6 }}>{right.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* DATA PROCESSING AGREEMENT */}
      <section style={{ padding: '4.5rem 1.5rem', background: p.sectionBg }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          {sectionHeader('Legal framework', 'Data Processing Agreement', 'How we handle, store, and protect your data as your processor.')}

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.25rem' }}>
            {dpaItems.map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div key={idx} variants={itemVariants} style={{ background: p.cardBg, border: `1px solid ${p.cardBorder}`, borderRadius: '1rem', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '1.75rem 1.5rem 1rem', textAlign: 'center' }}>
                    <div style={{ width: '52px', height: '52px', borderRadius: '0.85rem', background: 'linear-gradient(135deg, #0da27c 0%, #06b6d4 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                      <Icon size={24} />
                    </div>
                    <h3 style={{ fontSize: '1.02rem', fontWeight: 700, color: p.heading, margin: '0 0 0.6rem' }}>{item.title}</h3>
                    <p style={{ fontSize: '0.88rem', color: p.body, margin: 0, lineHeight: 1.65 }}>{item.description}</p>
                  </div>
                  <div style={{ marginTop: 'auto', padding: '0.75rem', background: p.footBg, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                    <CheckCircle size={13} color={p.numberColor} />
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: p.numberColor }}>UK GDPR compliant</span>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* DPO CTA — fixed dark panel, correct in both themes */}
      <section style={{ padding: '4.5rem 1.5rem', background: 'linear-gradient(135deg, #0f1d3a 0%, #16284f 100%)' }}>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}
        >
          <div style={{ width: '52px', height: '52px', borderRadius: '0.85rem', background: 'rgba(255,255,255,0.1)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}>
            <Mail size={22} />
          </div>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f1f5f9', margin: '0 0 0.75rem' }}>Questions about your data?</h2>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', margin: '0 0 1.5rem', lineHeight: 1.65 }}>
            Our Data Protection Officer is here to answer any privacy concerns or data subject requests.
          </p>
          <a href="mailto:thomas@effectivestaff.co.uk" style={{ display: 'inline-block', background: '#0da27c', color: 'white', fontWeight: 600, fontSize: '0.92rem', padding: '0.8rem 1.75rem', borderRadius: '0.65rem', textDecoration: 'none' }}>
            Contact our DPO
          </a>
        </motion.div>
      </section>
    </div>
  );
};

export default Gdpr;